import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Box, Container, Button, CircularProgress, Typography, TextField, Card, FormControl } from '@material-ui/core';

import { Icon } from '@iconify/react';
import baselinePersonAdd from '@iconify/icons-ic/baseline-person-add';
import { ComponentsLib } from '@payface-frontends-libs/components';
import { ExportButton } from '../../components/library/ExportButton';
import { getCsvDataClients } from '../../utils/clientsServices';
import Permissions from '../../hooks/Permissions';
import {
  getRegisteredCustomers,
  updateFilterOptions,
  resetPaginationData,
  resetClientsStates,
} from '../../redux/slices/clients';
import { RootState } from '../../redux/store';
import { resetDataList } from '../../redux/slices/global';
import { PATH_DASHBOARD } from '../../routes/paths';
import { ClientsDataList } from '../../components/_clients/ClientsDataList';
import { FilterButtons } from '../../components/library/FilterButtons';
import { resetCardsStates } from '../../redux/slices/cards';

const { Page } = ComponentsLib.Generics;

const Clients = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRequesting, setFilterRequesting] = useState(false);
  const [clearRequesting, setClearRequesting] = useState(false);
  const [exportRequesting, setExportRequesting] = useState(false);
  const { clients, _end, _order, _start, isRequestingClients, name, period } = useSelector(
    (state: RootState) => state.clients
  );
  const { reset } = useSelector((state: RootState) => state.global);

  useEffect(() => {
    if (reset === true) {
      void dispatch(resetDataList(false));
    }
    dispatch(
      getRegisteredCustomers({
        _end,
        _order,
        _sort: 'createdAt',
        _start,
        q: '',
        userInfo: name,
        getFaceUsageIntentions: true,
        period,
      })
    );
  }, [_order, reset, _end, name, period]); // eslint-disable-line

  if (!clients) {
    return <CircularProgress />;
  }

  const handleInvite = () => {
    history.push(`${PATH_DASHBOARD.clients.invite}`);
  };

  const handleExport = async () => {
    setExportRequesting(true);
    const end = 1000;
    const start = 0;
    const sort = 'createdAt';
    const data = await getCsvDataClients({
      _end: end,
      _order,
      _sort: sort,
      _start: start,
      userInfo: name,
      period,
    });
    setExportRequesting(false);
    return data;
  };

  const handleSearch = (event) => {
    const searchValue = event.target.value;
    setSearchTerm(searchValue);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (searchTerm !== '') {
      dispatch(resetPaginationData(true));
      dispatch(updateFilterOptions({ name: searchTerm, period }));
      setFilterRequesting(true);
    }
  };

  const handleCleanFilter = () => {
    setSearchTerm('');
    dispatch(resetPaginationData(true));
    dispatch(updateFilterOptions({ name: '', period: 'ALL' }));
    setClearRequesting(true);
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (isRequestingClients === false) {
      setFilterRequesting(false);
      setClearRequesting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRequestingClients]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (reset === true) {
      setSearchTerm('');
      setFilterRequesting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    resetClientsStates();
    resetCardsStates();
  }, []);

  return (
    <Page title="CRM > Clientes">
      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 3 }}>
          Clientes
        </Typography>
        <Card sx={{ display: 'flex', flexDirection: 'column', p: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              px: 1,
              py: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <form
              id="clientsForm"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '100%',
              }}
              onSubmit={(event) => handleSubmit(event)}
            >
              <FormControl sx={{ marginRight: 2 }}>
                <TextField
                  id="userInfo"
                  name="userInfo"
                  value={searchTerm}
                  label="Nome/CPF"
                  type="text"
                  variant="outlined"
                  sx={{ width: '380px' }}
                  onChange={(event) => handleSearch(event)}
                />
              </FormControl>
              <FilterButtons
                initialState=""
                filterRequesting={filterRequesting}
                clearRequesting={clearRequesting}
                setFilter={setSearchTerm}
                setClearRequesting={setClearRequesting}
                formId="clientsForm"
                updateFilter={updateFilterOptions}
                resetPagination={resetPaginationData}
                onClean={handleCleanFilter}
              />
            </form>

            <Box sx={{ width: 'auto' }}>
              {!Permissions.hasSeveralPlaces() && (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    type="button"
                    onClick={handleInvite}
                    startIcon={<Icon icon={baselinePersonAdd} style={{ marginRight: 4 }} />}
                    size="medium"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    Convidar Pessoa
                  </Button>
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', marginLeft: '8px', gap: 2, alignItems: 'center' }}>
              <ExportButton handleExport={handleExport} isRequesting={exportRequesting} name="clients" />
            </Box>
          </Box>
          <ClientsDataList />
        </Card>
      </Container>
    </Page>
  );
};

export default Clients;
